/**
 * @file Amend/resubmit modal for an open amendable no-match HI review.
 * @module pages/admin/hi-alerts/AmendedSearchModal
 *
 * B1.3H-3R-3 — UC.015 / HI_015_005818. Collects the five backend-allowed amended
 * criteria as transient form state, submits them to the authentication
 * amended-search endpoint and surfaces a safe, neutral outcome message. The
 * operator-entered criteria (including the DVA file number) exist only as React
 * form state for the lifetime of the open modal: they are never written to
 * localStorage/sessionStorage, a URL, persistent app state or the console, and
 * are cleared on both cancel and successful submit.
 *
 * B1.3H-3R-8 — aligns the visible labels with the MyGene patient/admin naming
 * convention (First Name before Last Name, Date of Birth, DVA File Number) and
 * captures the DOB in the Australian DD/MM/YYYY format. The five API payload keys
 * are unchanged and the date_of_birth value is converted back to ISO YYYY-MM-DD
 * before it reaches submitAmendedSearch.
 *
 * B1.3H-3R-11 — surfaces the category-only submit outcome. The amended-search
 * response already carries category-only `outcome` and `assignment_outcome`
 * labels; on success the modal now keeps itself open and replaces the criteria
 * form with a neutral result surface built by {@link describeAmendedSearchOutcome}
 * (which never emits a raw IHI/DVA/demographic, a match count or a response body,
 * and never implies an IHI was assigned). The transient criteria are cleared and
 * leave the DOM at that point, and the persistent reminder that the originating
 * HI_015_006105 no-match review remains open is shown alongside the outcome.
 *
 * CU-14ygtkj8z3m — UC.010 / HI_010_005818. The same modal hosts the registration
 * no-match resubmission: the stored review reason pins the Search Type (3, 4 or 5)
 * and therefore the fields rendered and the exact request built, with no type
 * selector. The lifecycle, stale-completion token and privacy guarantees are shared;
 * the request, result and error wording are distinct per contract.
 *
 * B2.4K review — the built submission reaches the request through a ref keyed by
 * submission sequence, never as TanStack mutation variables, so no criteria remain
 * in the mutation observer or MutationCache once the request settles.
 */

import React, { useEffect, useRef, useState } from "react";
import { Modal, Form, Input, Alert, Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import { extractValidationErrors } from "../../../utils/apiError";
import { validateMedicareNumber } from "../../../utils/healthcare/ValidateMedicareNumber";
import { normaliseMedicareNumberForApi } from "../../../utils/healthcare/NormaliseMedicareNumber";
import { submitAmendedSearch, submitRegistrationAmendedSearch, submitRevalidationAmendedSearch } from "../../../api/admin/hiAlertsApi";
import {
	AMEND_VARIANT_UC010_REGISTRATION_TYPE_3,
	AMEND_VARIANT_UC010_REGISTRATION_TYPE_4,
	AMEND_VARIANT_UC010_REGISTRATION_TYPE_5,
	AMEND_VARIANT_UC015_ADDRESS_TYPE_6,
	AMEND_VARIANT_UC015_DVA_REVALIDATION,
	AMEND_VARIANT_UC015_MEDICARE_TYPE_1,
	AMEND_VARIANT_UC015_MEDICARE_TYPE_2,
	AMEND_VARIANT_UC015_MEDICARE_TYPE_3,
	AmendableReviewVariant,
	AmendedSearchCriteria,
	AmendedSearchResult,
	IhiRevalidationReview,
	RegistrationAmendedSearchRequest,
	RegistrationAmendedSearchResult,
	RevalidationAmendedSearchRequest,
	RevalidationAmendedSearchResult,
	amendableReviewVariant,
} from "../../../types/admin/hiAlerts";
import { describeAmendedSearchOutcome, describeRevalidationAmendedSearchOutcome, REVIEW_REMAINS_OPEN_NOTE } from "./amendedSearchOutcome";
import {
	describeRegistrationAmendedSearchOutcome,
	REGISTRATION_REVIEW_REMAINS_OPEN_NOTE,
} from "./registrationAmendedSearchOutcome";

interface AmendedSearchModalProps {
	open: boolean;
	review: IhiRevalidationReview | null;
	onClose: () => void;
}

/** One submission, pinned to its contract so the result and error wording can never cross over. */
type Submission =
	| { kind: "uc015"; criteria: AmendedSearchCriteria }
	| { kind: "uc015_revalidation"; request: RevalidationAmendedSearchRequest }
	| { kind: "uc010"; request: RegistrationAmendedSearchRequest };

type SubmissionResult =
	| { kind: "uc015"; data: AmendedSearchResult }
	| { kind: "uc015_revalidation"; data: RevalidationAmendedSearchResult }
	| { kind: "uc010"; data: RegistrationAmendedSearchResult };

/** The only thing handed to useMutation: a sequence to look the submission up by, plus its contract for error wording. */
type SubmissionHandle = { seq: number; kind: Submission["kind"] };

/**
 * Identity of one amended-search submission: a monotonic sequence plus the target
 * review it was launched for. onMutate stamps it and the completion callbacks
 * re-check it, so a stale completion (one that finishes after the modal closed,
 * after the review changed, or after a newer submit superseded it) is ignored.
 */
type SubmissionToken = { seq: number; type: string; id: number };

type FieldKey =
	| "medicare_card_number"
	| "individual_reference_number"
	| "dva_file_number"
	| "given_name"
	| "family_name"
	| "date_of_birth"
	| "sex"
	| "address";

/** The fields each variant renders, in display order; the request builder below uses exactly the same set. */
const FIELDS_BY_VARIANT: Record<AmendableReviewVariant, FieldKey[]> = {
	[AMEND_VARIANT_UC015_DVA_REVALIDATION]: ["given_name", "family_name", "date_of_birth", "sex", "dva_file_number"],
	[AMEND_VARIANT_UC015_MEDICARE_TYPE_1]: ["medicare_card_number", "individual_reference_number", "given_name", "family_name", "date_of_birth", "sex"],
	[AMEND_VARIANT_UC015_MEDICARE_TYPE_2]: ["medicare_card_number", "individual_reference_number", "family_name", "date_of_birth", "sex"],
	[AMEND_VARIANT_UC015_MEDICARE_TYPE_3]: ["medicare_card_number", "given_name", "family_name", "date_of_birth", "sex"],
	[AMEND_VARIANT_UC015_ADDRESS_TYPE_6]: ["given_name", "family_name", "date_of_birth", "sex", "address"],
	[AMEND_VARIANT_UC010_REGISTRATION_TYPE_3]: ["medicare_card_number", "given_name", "family_name", "date_of_birth", "sex"],
	[AMEND_VARIANT_UC010_REGISTRATION_TYPE_4]: ["dva_file_number", "given_name", "family_name", "date_of_birth", "sex"],
	[AMEND_VARIANT_UC010_REGISTRATION_TYPE_5]: ["dva_file_number", "family_name", "date_of_birth", "sex"],
};

const TITLE_BY_VARIANT: Record<AmendableReviewVariant, string> = {
	[AMEND_VARIANT_UC015_DVA_REVALIDATION]: "Amend DVA IHI revalidation search",
	[AMEND_VARIANT_UC015_MEDICARE_TYPE_1]: "Amend Medicare IHI revalidation search (with IRN)",
	[AMEND_VARIANT_UC015_MEDICARE_TYPE_2]: "Amend Medicare IHI revalidation search (with IRN, single name)",
	[AMEND_VARIANT_UC015_MEDICARE_TYPE_3]: "Amend Medicare IHI revalidation search",
	[AMEND_VARIANT_UC015_ADDRESS_TYPE_6]: "Amend address IHI revalidation search",
	[AMEND_VARIANT_UC010_REGISTRATION_TYPE_3]: "Amend registration IHI search (Medicare)",
	[AMEND_VARIANT_UC010_REGISTRATION_TYPE_4]: "Amend registration IHI search (DVA)",
	[AMEND_VARIANT_UC010_REGISTRATION_TYPE_5]: "Amend registration IHI search (DVA, single name)",
};

const UC015_INTRO =
	"Operator-entered amended details are used for this submission only and are not stored. The original no-match review stays open for explicit operator resolution.";

const REGISTRATION_INTRO =
	"Operator-entered amended details are used for this registration search only and are not stored. The original registration no-match review stays open for explicit operator resolution.";

/**
 * Map an amended-search failure to a safe, neutral operator message keyed on the
 * backend's authoritative status code. The backend's own HI-policy wording (e.g.
 * "rejected by the Healthcare Identifiers search policy") is deliberately NOT
 * echoed, so the UI never implies live HI Service / DTSS execution. Per-field 422
 * validation messages are safe — they describe the operator's own form input —
 * and are surfaced via the shared extractor.
 */
const messageForError = (error: unknown): string => {
	if (axios.isAxiosError(error) && error.response) {
		const status = error.response.status;
		const data = error.response.data as { errors?: Record<string, string[]> } | undefined;

		if (status === 422 && data?.errors) {
			return extractValidationErrors(error).join(", ");
		}

		switch (status) {
			case 404:
				return "This review could not be found. It may already be resolved or no longer available.";
			case 409:
				return "This review is no longer open for an amended search.";
			case 422:
				return "The amended search details were not accepted. Please check the details and try again.";
			case 502:
				return "The amended search could not be completed at this time. Please try again later.";
			default:
				return "Could not submit the amended search. Please try again.";
		}
	}
	return "Could not submit the amended search. Please try again.";
};

/**
 * CU-14ygtkj8z3m: the registration contract's fixed wording. A 409 is a bounded lifecycle or safety refusal
 * (closed, not eligible, current IHI present, name basis changed) and is never reported only as "closed".
 */
const registrationMessageForError = (error: unknown): string => {
	if (axios.isAxiosError(error) && error.response) {
		const status = error.response.status;
		const data = error.response.data as { errors?: Record<string, string[]> } | undefined;

		if (status === 422 && data?.errors) {
			return extractValidationErrors(error).join(", ");
		}

		switch (status) {
			case 401:
				return "You are no longer signed in. Sign in again before submitting the amended registration search.";
			case 403:
				return "You are not permitted to submit an amended registration search.";
			case 404:
				return "This review could not be found. It may already be resolved or no longer available.";
			case 409:
				return "This amended registration search can no longer be submitted with the current review state. Refresh the review before taking further action.";
			case 422:
				return "The amended registration search details were not accepted. Please check the details and try again.";
			case 502:
				return "The amended registration search could not be completed at this time. Please try again later.";
			case 503:
				return "The amended registration search was not performed because the action could not be recorded. Please try again later.";
			default:
				return "Could not submit the amended registration search. Please try again.";
		}
	}
	return "Could not submit the amended registration search. Please try again.";
};

/** CU-14ygtkj98pb: a Medicare/address 409 is a bounded refusal (closed, basis changed, no verified IHI), not only "closed". */
const revalidationMessageForError = (error: unknown): string =>
	axios.isAxiosError(error) && error.response?.status === 409
		? "This amended search can no longer be submitted with the current review or patient state. Refresh the review before taking further action."
		: messageForError(error);

// The DOB field is captured in the Australian DD/MM/YYYY visible format used
// across the MyGene patient/admin screens, while the amended-search API contract
// stays ISO YYYY-MM-DD. This pattern gates the visible input and drives the
// conversion below, so the date_of_birth payload key and value remain unchanged.
const DOB_DDMMYYYY = /^(\d{2})\/(\d{2})\/(\d{4})$/;

// Convert an operator-entered DD/MM/YYYY value to the ISO YYYY-MM-DD the backend
// expects. The value has already passed the DOB_DDMMYYYY form rule by the time it
// reaches here; the guard only keeps the helper safe if called defensively.
const toApiDateOfBirth = (value: string): string => {
	const match = DOB_DDMMYYYY.exec(value);
	if (!match) {
		return value;
	}
	const [, day, month, year] = match;
	return `${year}-${month}-${day}`;
};

const validateMedicareCardNumber = (_rule: unknown, value: unknown) => {
	if (typeof value !== "string" || value.trim() === "") {
		return Promise.resolve();
	}
	const result = validateMedicareNumber(value);
	return result.isValid ? Promise.resolve() : Promise.reject(new Error(result.error));
};

/**
 * Build the submission explicitly from the variant's own fields only — never spread
 * the raw form object — so no extra value, and no other technique's identifier, can
 * reach the request. Type 5 constructs no given_name key at all.
 */
const buildSubmission = (variant: AmendableReviewVariant, values: Record<string, unknown>): Submission => {
	const text = (key: FieldKey): string => String(values[key] ?? "").trim();
	const dateOfBirth = toApiDateOfBirth(text("date_of_birth"));

	switch (variant) {
		case AMEND_VARIANT_UC015_DVA_REVALIDATION:
			return {
				kind: "uc015",
				criteria: {
					family_name: text("family_name"),
					given_name: text("given_name"),
					date_of_birth: dateOfBirth,
					sex: text("sex"),
					dva_file_number: text("dva_file_number"),
				},
			};
		case AMEND_VARIANT_UC015_MEDICARE_TYPE_1:
			return {
				kind: "uc015_revalidation",
				request: {
					variant,
					criteria: {
						medicare_card_number: normaliseMedicareNumberForApi(text("medicare_card_number")),
						individual_reference_number: text("individual_reference_number"),
						given_name: text("given_name"),
						family_name: text("family_name"),
						date_of_birth: dateOfBirth,
						sex: text("sex"),
					},
				},
			};
		case AMEND_VARIANT_UC015_MEDICARE_TYPE_2:
			return {
				kind: "uc015_revalidation",
				request: {
					variant,
					criteria: {
						medicare_card_number: normaliseMedicareNumberForApi(text("medicare_card_number")),
						individual_reference_number: text("individual_reference_number"),
						family_name: text("family_name"),
						date_of_birth: dateOfBirth,
						sex: text("sex"),
					},
				},
			};
		case AMEND_VARIANT_UC015_MEDICARE_TYPE_3:
			return {
				kind: "uc015_revalidation",
				request: {
					variant,
					criteria: {
						medicare_card_number: normaliseMedicareNumberForApi(text("medicare_card_number")),
						given_name: text("given_name"),
						family_name: text("family_name"),
						date_of_birth: dateOfBirth,
						sex: text("sex"),
					},
				},
			};
		case AMEND_VARIANT_UC015_ADDRESS_TYPE_6:
			return {
				kind: "uc015_revalidation",
				request: {
					variant,
					criteria: {
						given_name: text("given_name"),
						family_name: text("family_name"),
						date_of_birth: dateOfBirth,
						sex: text("sex"),
						address: text("address"),
					},
				},
			};
		case AMEND_VARIANT_UC010_REGISTRATION_TYPE_3:
			return {
				kind: "uc010",
				request: {
					variant,
					criteria: {
						medicare_card_number: normaliseMedicareNumberForApi(text("medicare_card_number")),
						given_name: text("given_name"),
						family_name: text("family_name"),
						date_of_birth: dateOfBirth,
						sex: text("sex"),
					},
				},
			};
		case AMEND_VARIANT_UC010_REGISTRATION_TYPE_4:
			return {
				kind: "uc010",
				request: {
					variant,
					criteria: {
						dva_file_number: text("dva_file_number"),
						given_name: text("given_name"),
						family_name: text("family_name"),
						date_of_birth: dateOfBirth,
						sex: text("sex"),
					},
				},
			};
		case AMEND_VARIANT_UC010_REGISTRATION_TYPE_5:
			return {
				kind: "uc010",
				request: {
					variant,
					criteria: {
						dva_file_number: text("dva_file_number"),
						family_name: text("family_name"),
						date_of_birth: dateOfBirth,
						sex: text("sex"),
					},
				},
			};
	}
};

const renderField = (key: FieldKey): React.ReactNode => {
	switch (key) {
		case "medicare_card_number":
			return (
				<Form.Item
					key={key}
					label="Medicare Card Number"
					name="medicare_card_number"
					rules={[
						{ required: true, message: "Medicare card number is required" },
						{ validator: validateMedicareCardNumber },
					]}
				>
					<Input autoComplete="off" placeholder="Medicare Card Number" />
				</Form.Item>
			);
		case "individual_reference_number":
			return (
				<Form.Item
					key={key}
					label="Medicare Reference Number (IRN)"
					name="individual_reference_number"
					rules={[
						{ required: true, message: "Medicare reference number is required" },
						{ pattern: /^[1-9]$/, message: "Medicare reference number must be a single digit from 1 to 9" },
					]}
				>
					<Input autoComplete="off" placeholder="IRN" />
				</Form.Item>
			);
		case "address":
			return (
				<Form.Item
					key={key}
					label="Address"
					name="address"
					rules={[
						{ required: true, message: "Address is required" },
						{ max: 255, message: "Address must be 255 characters or fewer" },
					]}
				>
					<Input autoComplete="off" placeholder="Address" />
				</Form.Item>
			);
		case "dva_file_number":
			return (
				<Form.Item
					key={key}
					label="DVA File Number"
					name="dva_file_number"
					rules={[
						{ required: true, message: "DVA file number is required" },
						{ max: 32, message: "DVA file number must be 32 characters or fewer" },
					]}
				>
					<Input autoComplete="off" placeholder="DVA File Number" />
				</Form.Item>
			);
		case "given_name":
			return (
				<Form.Item
					key={key}
					label="First Name"
					name="given_name"
					rules={[
						{ required: true, message: "First name is required" },
						{ max: 100, message: "First name must be 100 characters or fewer" },
					]}
				>
					<Input autoComplete="off" placeholder="First Name" />
				</Form.Item>
			);
		case "family_name":
			return (
				<Form.Item
					key={key}
					label="Last Name"
					name="family_name"
					rules={[
						{ required: true, message: "Last name is required" },
						{ max: 100, message: "Last name must be 100 characters or fewer" },
					]}
				>
					<Input autoComplete="off" placeholder="Last Name" />
				</Form.Item>
			);
		case "date_of_birth":
			return (
				<Form.Item
					key={key}
					label="Date of Birth"
					name="date_of_birth"
					rules={[
						{ required: true, message: "Date of birth is required" },
						{ pattern: DOB_DDMMYYYY, message: "Date of birth must be in DD/MM/YYYY format" },
					]}
				>
					<Input autoComplete="off" placeholder="DD/MM/YYYY" />
				</Form.Item>
			);
		case "sex":
			return (
				<Form.Item
					key={key}
					label="Sex"
					name="sex"
					rules={[
						{ required: true, message: "Sex is required" },
						{ max: 10, message: "Sex must be 10 characters or fewer" },
					]}
				>
					<Input autoComplete="off" placeholder="Sex" />
				</Form.Item>
			);
	}
};

const AmendedSearchModal: React.FC<AmendedSearchModalProps> = ({ open, review, onClose }) => {
	const [form] = Form.useForm();
	const [error, setError] = useState<string>("");
	// The category-only submit result. While set, the modal shows the outcome
	// surface instead of the form; it holds only bounded category labels and
	// internal ids — never demographics or a request/response body.
	const [result, setResult] = useState<SubmissionResult | null>(null);
	const queryClient = useQueryClient();

	// Which contract this review takes, read from the projected reason and requirement anchor only (never from form contents).
	const variant = review ? amendableReviewVariant(review) : null;

	// Identifies the in-flight amended-search submission so a completion that
	// resolves after the modal was closed, the target review changed, or a newer
	// submission superseded it is ignored instead of populating a stale result.
	// (The component stays mounted under HiAlertReviewPage between opens, so a
	// React unmount does not cancel an in-flight completion.)
	const submissionSeqRef = useRef(0);
	const activeSubmissionRef = useRef<SubmissionToken | null>(null);
	// Built submissions awaiting their request, keyed by seq; each is removed the moment its request starts.
	const pendingSubmissionsRef = useRef(new Map<number, Submission>());

	// True only for the submission that is still the active one for the currently
	// open review. Relies solely on the live ref (cleared on close / review change /
	// supersede), never on a possibly-stale render closure.
	const completionIsActive = (token?: SubmissionToken): boolean => {
		const active = activeSubmissionRef.current;
		return (
			token !== undefined &&
			active !== null &&
			active.seq === token.seq &&
			active.type === token.type &&
			active.id === token.id
		);
	};

	// Reset transient modal state and abandon any in-flight submission whenever the
	// modal opens/closes or the target review changes, so a fresh open never shows
	// a previous review's outcome and a superseded completion is ignored below.
	useEffect(() => {
		activeSubmissionRef.current = null;
		setError("");
		setResult(null);
	}, [open, review?.type, review?.id]);

	// Clear the transient operator-entered criteria and reset modal state. Called
	// on both cancel and closing the result surface so the amended details never
	// persist in component state between opens. resetFields is skipped once a
	// result is showing because the form has already been reset and unmounted.
	const clearAndClose = () => {
		if (!result) {
			form.resetFields();
		}
		// Abandon any in-flight submission so its completion cannot repopulate state
		// after the modal has closed.
		activeSubmissionRef.current = null;
		setError("");
		setResult(null);
		onClose();
	};

	const mutation = useMutation<SubmissionResult, unknown, SubmissionHandle, { token: SubmissionToken }>({
		mutationFn: async (handle: SubmissionHandle): Promise<SubmissionResult> => {
			// Take the submission out of the handoff map before anything else so it lives only for this request.
			const submission = pendingSubmissionsRef.current.get(handle.seq);
			pendingSubmissionsRef.current.delete(handle.seq);
			if (!review || !submission) {
				throw new Error("No review selected");
			}
			if (submission.kind === "uc015") {
				return { kind: "uc015", data: await submitAmendedSearch(review.type, review.id, submission.criteria) };
			}
			if (submission.kind === "uc015_revalidation") {
				return { kind: "uc015_revalidation", data: await submitRevalidationAmendedSearch(review.type, review.id, submission.request) };
			}
			return { kind: "uc010", data: await submitRegistrationAmendedSearch(review.type, review.id, submission.request) };
		},
		// Stamp this submission with a monotonic token bound to the target review.
		// The completion callbacks re-check it so a request that resolves after the
		// modal closed, the review changed, or a newer submit superseded it cannot
		// populate a stale result.
		onMutate: (handle) => {
			const token: SubmissionToken = {
				seq: handle.seq,
				type: review?.type ?? "",
				id: review?.id ?? -1,
			};
			activeSubmissionRef.current = token;
			return { token };
		},
		onSuccess: (data, _submission, context) => {
			// The backend action ran regardless of the modal's current state, so keep
			// the list and any open detail drawer fresh either way. A blocked-different
			// -IHI outcome can open a separate review the list should now show.
			queryClient.invalidateQueries({ queryKey: ["hi-alerts"] });
			queryClient.invalidateQueries({ queryKey: ["hi-alert"] });

			// Only the still-active submission for the currently open review may
			// populate the visible result — ignore a stale completion (the reported
			// race: submit, then close the modal while pending).
			if (!completionIsActive(context?.token)) {
				return;
			}

			// Surface the already-returned category-only outcome in the modal. The
			// transient criteria are cleared immediately (they then leave the DOM as
			// the result view replaces the form); the originating no-match review is
			// left open and the result surface repeats that reminder.
			activeSubmissionRef.current = null;
			setResult(data);
			form.resetFields();
		},
		onError: (err, handle, context) => {
			// Same staleness guard: a late failure must not surface an error on a modal
			// that has since been closed or reopened for a different review.
			if (!completionIsActive(context?.token)) {
				return;
			}
			activeSubmissionRef.current = null;
			setError(
				handle.kind === "uc010"
					? registrationMessageForError(err)
					: handle.kind === "uc015_revalidation"
					? revalidationMessageForError(err)
					: messageForError(err)
			);
		},
	});

	const handleSubmit = async () => {
		if (variant === null) {
			return;
		}

		let values: Record<string, unknown>;
		try {
			values = await form.validateFields();
		} catch {
			// antd surfaces the per-field validation messages inline; nothing else to do.
			return;
		}

		setError("");
		// One seq per click: the submission waits in the map and the mutation carries only its handle.
		const submission = buildSubmission(variant, values);
		const seq = (submissionSeqRef.current += 1);
		pendingSubmissionsRef.current.set(seq, submission);
		mutation.mutate({ seq, kind: submission.kind });
	};

	// Neutral, category-only wording for the returned outcome. Only computed once a
	// result exists; the raw category tokens themselves are never rendered.
	const outcomeMessage =
		result === null
			? null
			: result.kind === "uc015"
			? describeAmendedSearchOutcome(result.data.outcome, result.data.assignment_outcome)
			: result.kind === "uc015_revalidation"
			? describeRevalidationAmendedSearchOutcome(result.data.outcome, result.data.current_ihi_relation, result.data.transition_action)
			: describeRegistrationAmendedSearchOutcome(result.data.outcome, result.data.assignment);
	const remainsOpenNote = result?.kind === "uc010" ? REGISTRATION_REVIEW_REMAINS_OPEN_NOTE : REVIEW_REMAINS_OPEN_NOTE;
	const isRegistration =
		variant === AMEND_VARIANT_UC010_REGISTRATION_TYPE_3 ||
		variant === AMEND_VARIANT_UC010_REGISTRATION_TYPE_4 ||
		variant === AMEND_VARIANT_UC010_REGISTRATION_TYPE_5;

	return (
		<Modal
			title={variant === null ? "Amend search" : TITLE_BY_VARIANT[variant]}
			open={open}
			onCancel={clearAndClose}
			width={560}
			destroyOnClose
			maskClosable={false}
			// Belt-and-suspenders only: block the close icon / Esc while a submit is in
			// flight. The submission-token guard above is the authoritative fix for the
			// stale-completion race and does not depend on this.
			closable={!mutation.isPending}
			keyboard={!mutation.isPending}
			footer={
				result
					? [
							<Button key="close" type="primary" onClick={clearAndClose}>
								Close
							</Button>,
					  ]
					: [
							<Button key="cancel" onClick={clearAndClose} disabled={mutation.isPending}>
								Cancel
							</Button>,
							<Button
								key="submit"
								type="primary"
								icon={<SearchOutlined />}
								loading={mutation.isPending}
								disabled={variant === null}
								onClick={handleSubmit}
							>
								Submit amended search
							</Button>,
					  ]
			}
		>
			{result && outcomeMessage ? (
				<>
					<Alert
						type={outcomeMessage.severity}
						showIcon
						style={{ marginBottom: 16 }}
						message={outcomeMessage.title}
						description={outcomeMessage.detail}
					/>
					<Alert type="info" showIcon message={remainsOpenNote} />
				</>
			) : variant === null ? (
				<Alert type="warning" showIcon message="This review is not open for an amended search." />
			) : (
				<>
					<Alert
						type="info"
						showIcon
						style={{ marginBottom: 16 }}
						message={isRegistration ? REGISTRATION_INTRO : UC015_INTRO}
					/>

					{error && (
						<Alert
							type="error"
							closable
							onClose={() => setError("")}
							style={{ marginBottom: 16 }}
							message={error}
						/>
					)}

					<Form form={form} layout="vertical">
						{FIELDS_BY_VARIANT[variant].map(renderField)}
					</Form>
				</>
			)}
		</Modal>
	);
};

export default AmendedSearchModal;
